// src/Dashboard/admin/sections/RemindersSection.jsx
import { useEffect, useMemo, useState } from "react";
import {
  RefreshCw,
  Bell,
  Search,
  Building2,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Mail,
  AlertTriangle,
  Filter,
} from "lucide-react";

import { listLifecycleReminderLogs } from "../../../services/lifecycleApi";

function safeLower(v) {
  return String(v || "").trim().toLowerCase();
}

function Badge({ tone = "neutral", icon: Icon, label }) {
  const styles =
    tone === "success"
      ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200 border-green-200 dark:border-green-800"
      : tone === "danger"
        ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-200 border-red-200 dark:border-red-800"
        : tone === "warn"
          ? "bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200 border-amber-200 dark:border-amber-800"
          : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700";

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] font-semibold ${styles}`}>
      {Icon ? <Icon size={14} /> : null}
      {label}
    </span>
  );
}

export default function RemindersSection({
  getToken,
  scopeLabel,
  refreshNonce,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);

  // paging + filters
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [bucket, setBucket] = useState("");
  const [channel, setChannel] = useState("");

  async function load({ nextOffset = offset, nextLimit = limit, reset = false } = {}) {
    try {
      setError("");
      setLoading(true);

      const token = await getToken?.();
      const data = await listLifecycleReminderLogs(token, {
        limit: nextLimit,
        offset: nextOffset,
        q: q.trim(),
        status: safeLower(status),
        bucket: safeLower(bucket),
        channel: safeLower(channel),
      });

      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(data?.total ?? null);

      if (reset) setOffset(0);
      else setOffset(nextOffset);
      setLimit(nextLimit);
    } catch (e) {
      console.error("[RemindersSection] load error:", e);
      setError(e.message || "Failed to load reminder logs");
      setRows([]);
      setTotal(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ nextOffset: 0, reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce]);

  const summary = useMemo(() => {
    const arr = Array.isArray(rows) ? rows : [];
    const sent = arr.filter((r) => safeLower(r.status) === "sent").length;
    const failed = arr.filter((r) => safeLower(r.status) === "failed").length;
    const skipped = arr.filter((r) => safeLower(r.status) === "skipped").length;
    const sending = arr.filter((r) => safeLower(r.status) === "sending").length;
    return { sent, failed, skipped, sending };
  }, [rows]);

  const scopeChips = useMemo(() => {
    if (scopeLabel) return [scopeLabel];
    return [];
  }, [scopeLabel]);

  const canPrev = offset > 0;
  const canNext = rows.length >= limit && (total == null ? true : offset + limit < total);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Reminders</h2>
            <p className="text-xs text-slate-500">
              Logs for automated lifecycle reminders (Day 30, 40, 50, 59). Graduates are not deleted.
            </p>
          </div>

          <button
            onClick={() => load({ nextOffset: offset })}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
          <Building2 size={14} />
          <span>
            Scope:{" "}
            <span className="font-semibold">
              {(scopeChips || []).length ? scopeChips.join(" • ") : "Your enforced admin scope"}
            </span>
          </span>
        </div>

        {error ? (
          <div className="mt-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-100 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5" />
            <div className="min-w-0">{error}</div>
          </div>
        ) : null}

        {/* Filters */}
        <div className="mt-5 grid md:grid-cols-6 gap-3">
          <div className="md:col-span-2 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name / email / phone…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
            />
          </div>

          <select
            value={bucket}
            onChange={(e) => setBucket(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
          >
            <option value="">All buckets</option>
            <option value="day30">day30</option>
            <option value="day40">day40</option>
            <option value="day50">day50</option>
            <option value="day59">day59</option>
          </select>

          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
          >
            <option value="">All channels</option>
            <option value="sms">SMS</option>
            <option value="email">Email</option>
          </select>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
          >
            <option value="">All statuses</option>
            <option value="sending">sending</option>
            <option value="sent">sent</option>
            <option value="failed">failed</option>
            <option value="skipped">skipped</option>
          </select>

          <select
            value={String(limit)}
            onChange={(e) => setLimit(Number(e.target.value) || 100)}
            className="px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
            title="Page size"
          >
            <option value="50">50 / page</option>
            <option value="100">100 / page</option>
            <option value="200">200 / page</option>
          </select>
        </div>

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <Filter size={14} />
            Tip: filters are server-side; logs show actual send results (with per-user buckets).
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setQ("");
                setStatus("");
                setBucket("");
                setChannel("");
                load({ nextOffset: 0, nextLimit: limit, reset: true });
              }}
              className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              Clear
            </button>

            <button
              onClick={() => load({ nextOffset: 0, nextLimit: limit, reset: true })}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 text-sm font-semibold"
            >
              Go
            </button>
          </div>
        </div>

        {/* Mini counters */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <MiniCounter icon={CheckCircle2} title="Sent" value={summary.sent} />
          <MiniCounter icon={XCircle} title="Failed" value={summary.failed} />
          <MiniCounter icon={Bell} title="Skipped" value={summary.skipped} />
          <MiniCounter icon={RefreshCw} title="Sending" value={summary.sending} />
        </div>
      </div>

      {/* Logs table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Reminder Logs</h3>
          <div className="text-xs text-slate-500">
            {total != null ? `Total: ${total} • ` : ""}
            Offset: {offset}
          </div>
        </div>

        <div className="mt-4 overflow-auto rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/70 text-slate-700 dark:text-slate-200">
              <tr>
                <th className="p-3 text-left">Learner</th>
                <th className="p-3 text-center">Bucket</th>
                <th className="p-3 text-center">Channels</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-left">Sent At</th>
                <th className="p-3 text-left">Error</th>
              </tr>
            </thead>

            <tbody>
              {(rows || []).map((r) => {
                const st = safeLower(r.status);
                const tone =
                  st === "sent" ? "success" : st === "failed" ? "danger" : st === "skipped" ? "warn" : "neutral";

                const channels = Array.isArray(r.channels) ? r.channels : [];
                const user = r.users || {};
                const name = user.full_name || "—";
                const email = user.email || "—";
                const phone = user.phone || "";

                return (
                  <tr
                    key={r.id || `${r.user_id}-${r.bucket}-${r.sent_at}`}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="p-3">
                      <div className="font-semibold truncate">{name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {email}
                        {phone ? ` • ${phone}` : ""}
                      </div>
                      <div className="text-[11px] text-slate-400 font-mono truncate">{r.user_id}</div>
                    </td>

                    <td className="p-3 text-center">
                      <Badge tone="neutral" icon={Bell} label={String(r.bucket || "—").toUpperCase()} />
                    </td>

                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {channels.includes("sms") ? (
                          <Badge tone="neutral" icon={MessageSquare} label="SMS" />
                        ) : null}
                        {channels.includes("email") ? (
                          <Badge tone="neutral" icon={Mail} label="EMAIL" />
                        ) : null}
                        {channels.length === 0 ? <span className="text-slate-400">—</span> : null}
                      </div>
                    </td>

                    <td className="p-3 text-center">
                      <Badge
                        tone={tone}
                        icon={st === "sent" ? CheckCircle2 : XCircle}
                        label={st || "—"}
                      />
                    </td>

                    <td className="p-3">
                      <div className="text-xs text-slate-500 truncate">
                        {r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}
                      </div>
                    </td>

                    <td className="p-3">
                      <div className="text-xs text-slate-500 truncate max-w-[420px]">
                        {r.error || "—"}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && (!rows || rows.length === 0) ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">
                    No reminder logs yet.
                  </td>
                </tr>
              ) : null}

              {loading ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
          <button
            onClick={() => canPrev && load({ nextOffset: Math.max(0, offset - limit) })}
            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
            disabled={!canPrev || loading}
          >
            Prev
          </button>

          <span>
            Showing {rows.length} • Offset {offset}
          </span>

          <button
            onClick={() => canNext && load({ nextOffset: offset + limit })}
            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
            disabled={!canNext || loading}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function MiniCounter({ icon: Icon, title, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60">
          <Icon size={18} className="text-slate-700 dark:text-slate-200" />
        </div>
        <div>
          <div className="text-xs text-slate-500">{title}</div>
          <div className="text-base font-semibold">{value}</div>
        </div>
      </div>
    </div>
  );
}
